import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import PublicSiteChrome from './PublicSiteChrome';
import { usePageMeta } from '../../lib/page-meta';
import {
  buildBreadcrumbSchema,
  buildWebPageSchema,
  SEO_BRAND_NAME,
  type StructuredDataSchema,
  type StructuredPageType,
} from '../../lib/seo';

interface PublicContentPageProps {
  metaTitle: string;
  metaDescription: string;
  canonicalPath: string;
  eyebrow: string;
  title: string;
  intro: string;
  children: ReactNode;
  aside?: ReactNode;
  schemaType?: StructuredPageType;
  metaType?: 'website' | 'article';
  structuredData?: StructuredDataSchema[];
}

interface PublicCtaLinkProps {
  href: string;
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost';
  className?: string;
}

function joinClassNames(...classNames: Array<string | undefined>): string {
  return classNames.filter(Boolean).join(' ');
}

export function PublicCtaLink({
  href,
  children,
  variant = 'secondary',
  className,
}: PublicCtaLinkProps) {
  const resolvedClassName = joinClassNames(
    'acc-public-button',
    `acc-public-button--${variant}`,
    className,
  );

  if (href.startsWith('mailto:') || href.startsWith('/#') || href.startsWith('#')) {
    return (
      <a href={href} className={resolvedClassName}>
        {children}
      </a>
    );
  }

  return (
    <Link to={href} className={resolvedClassName}>
      {children}
    </Link>
  );
}

export default function PublicContentPage({
  metaTitle,
  metaDescription,
  canonicalPath,
  eyebrow,
  title,
  intro,
  children,
  aside,
  schemaType = 'WebPage',
  metaType = 'website',
  structuredData = [],
}: PublicContentPageProps) {
  usePageMeta({
    title: metaTitle,
    description: metaDescription,
    canonicalPath,
    type: metaType,
    structuredData: [
      buildWebPageSchema({
        title,
        description: metaDescription,
        path: canonicalPath,
        pageType: schemaType,
      }),
      buildBreadcrumbSchema([
        { name: SEO_BRAND_NAME, path: '/' },
        { name: title, path: canonicalPath },
      ]),
      ...structuredData,
    ],
  });

  return (
    <PublicSiteChrome>
      <div className="acc-public-frame">
        <section className="acc-public-section acc-public-page-hero-section" aria-labelledby="public-page-title">
          <div className="acc-public-page-hero">
            <div className="acc-public-kicker">{eyebrow}</div>
            <h1 id="public-page-title" className="acc-public-page-title">
              {title}
            </h1>
            <p className="acc-public-page-intro">{intro}</p>
          </div>
        </section>

        <section className="acc-public-section acc-public-page-body-section">
          <div className={joinClassNames('acc-public-page-layout', aside ? undefined : 'acc-public-page-layout--single')}>
            <div className="acc-public-page-main">{children}</div>
            {aside && <aside className="acc-public-page-aside">{aside}</aside>}
          </div>
        </section>
      </div>
    </PublicSiteChrome>
  );
}
